import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, gbp2 } from "@/lib/api";

export default function SelfCancel() {
  const { bid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const initialMode = params.get("mode") === "reschedule" ? "reschedule" : "choose";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [rescheduled, setRescheduled] = useState(null);

  const load = () => api.get(`/bookings/${bid}/self-cancel-info`, { params: { token } })
    .then((r) => setState({ loading: false, error: null, data: r.data }))
    .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Link invalid", data: null }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid, token]);

  const cancelNow = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`/bookings/${bid}/self-cancel?token=${encodeURIComponent(token)}`);
      setDone(r.data);
      toast.success("Your appointment has been cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    }
    setConfirming(false);
  };

  if (state.loading) return <main className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><p className="mono-label">Loading…</p></main>;
  if (state.error) return <main className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-8 text-center"><div><h1 className="font-serif text-3xl">Link no longer valid</h1><p className="mt-3 text-stone-600">{state.error}</p></div></main>;

  const d = state.data;

  if (rescheduled) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] px-6 py-16" data-testid="self-reschedule-done">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-3 py-1 rounded-sm border hairline"><Check size={16} /><span className="mono-label">Appointment Rescheduled</span></div>
          <h1 className="mt-6 font-serif text-4xl font-light text-stone-900">Your new appointment is booked</h1>
          <p className="mt-3 text-stone-600">Booking <span className="font-mono">{d.reference}</span></p>
          <p className="mt-2 text-stone-700">Previously <span className="line-through text-stone-400">{rescheduled.from.date} · {rescheduled.from.time}</span></p>
          <p className="mt-1 text-stone-900 font-medium text-lg">Now on {rescheduled.to.date} at {rescheduled.to.time}</p>
          <p className="mt-8 text-xs text-stone-500">A confirmation email is on its way.</p>
        </div>
      </main>
    );
  }

  if (done || d.already_cancelled) {
    const late = done?.late_cancel ?? d.late_cancel;
    const forfeited = done?.deposit_forfeited;
    return (
      <main className="min-h-screen bg-[#FAF9F6] px-6 py-16" data-testid="self-cancel-done">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-3 py-1 rounded-sm border hairline"><Check size={16} /><span className="mono-label">Appointment Cancelled</span></div>
          <h1 className="mt-6 font-serif text-4xl font-light text-stone-900">Your booking is cancelled</h1>
          <p className="mt-3 text-stone-600">Booking <span className="font-mono">{d.reference}</span> · {d.date} at {d.time}</p>
          {late && forfeited && (
            <p className="mt-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3 max-w-lg mx-auto" data-testid="late-deposit-notice">
              Because you cancelled inside the 48-hour window, your deposit has been retained as per our cancellation policy.
            </p>
          )}
          {!late && d.amount_paid > 0 && (
            <p className="mt-6 text-sm text-stone-600 max-w-lg mx-auto">Your deposit can be transferred to a future appointment — please reply to your confirmation email to rebook.</p>
          )}
          <p className="mt-8 text-xs text-stone-500">If this was a mistake, please contact us and we'll do our best to help.</p>
        </div>
      </main>
    );
  }

  const late = d.late_cancel;
  const canReschedule = !!d.can_self_reschedule;

  return (
    <main className="min-h-screen bg-[#FAF9F6] pb-24" data-testid="self-cancel-screen">
      <header className="border-b hairline bg-white">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-6 py-4">
          <span className="font-serif text-2xl">CLAÈ</span>
          <span className="mono-label">{mode === "reschedule" ? "Reschedule Appointment" : "Cancel Appointment"}</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 pt-12 fade-in">
        <p className="mono-label">Booking {d.reference}</p>
        <h1 className="font-serif text-4xl font-light text-stone-900 mt-2">
          {mode === "reschedule" ? "Choose a new date & time" : "Cancel your CLAÈ appointment?"}
        </h1>
        <p className="mt-3 text-stone-600 text-sm">Hi {d.client_name.split(" ")[0]}, please review the details below before confirming.</p>

        <div className="mt-8 border hairline bg-white p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-stone-600">Current date</span><span>{d.date}</span></div>
          <div className="flex justify-between"><span className="text-stone-600">Current time</span><span>{d.time}</span></div>
          <div className="flex justify-between"><span className="text-stone-600">Treatments</span><span className="text-right">{d.items.map(i => `${i.name} × ${i.quantity}`).join(", ")}</span></div>
          <div className="flex justify-between pt-2 border-t hairline"><span className="text-stone-600">Deposit paid</span><span>{gbp2(d.amount_paid)}</span></div>
        </div>

        {mode === "reschedule" ? (
          canReschedule ? (
            <ReschedulePanel
              bid={bid}
              token={token}
              duration={d.duration_min || 60}
              currentDate={d.date}
              onCancel={() => setMode("choose")}
              onDone={(payload) => { setRescheduled(payload); }}
            />
          ) : (
            <div className="mt-8 border border-amber-300 bg-amber-50 p-6" data-testid="reschedule-blocked">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="mono-label text-amber-900">Online rescheduling unavailable</p>
                  <p className="mt-2 text-sm text-stone-800 leading-relaxed">
                    Online rescheduling is no longer available for this appointment because it is less than 24 hours away. Please contact CLAÈ Aesthetics directly for assistance.
                  </p>
                  <button
                    data-testid="back-to-cancel"
                    onClick={() => setMode("choose")}
                    className="mt-4 mono-label text-stone-700 hover:text-stone-900 underline text-xs"
                  >
                    ← Back to appointment options
                  </button>
                </div>
              </div>
            </div>
          )
        ) : (
          <>
            {late ? (
              <div className="mt-8 border border-amber-300 bg-amber-50 p-6" data-testid="late-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="mono-label text-amber-900">Inside the 48-hour window</p>
                    <p className="mt-2 text-sm text-stone-800 leading-relaxed">
                      Your appointment is in <strong>{d.hours_until != null ? `${d.hours_until.toFixed(1)} hours` : "less than 48 hours"}</strong>.
                      Cancelling now means your deposit of <strong>{gbp2(d.amount_paid)}</strong> will be retained, and any referral reward code on your account will be revoked.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-8 border hairline bg-white p-6" data-testid="early-notice">
                <p className="mono-label">Outside the 48-hour window</p>
                <p className="mt-2 text-sm text-stone-700">You're cancelling with plenty of notice, so your deposit can be transferred to a future appointment. Reply to your confirmation email to rebook.</p>
              </div>
            )}

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                data-testid="confirm-cancel"
                disabled={confirming}
                onClick={cancelNow}
                className={`py-4 mono-label transition text-white disabled:opacity-40 ${late ? "bg-amber-700 hover:bg-amber-800" : "bg-stone-900 hover:bg-stone-700"}`}
                style={{ color: "#fff" }}
              >
                {confirming ? "Cancelling…" : "Confirm Cancellation"}
              </button>
              {canReschedule && (
                <button
                  data-testid="open-reschedule"
                  onClick={() => setMode("reschedule")}
                  className="py-4 mono-label border border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white transition inline-flex items-center justify-center gap-2"
                >
                  <CalendarClock size={16} /> Reschedule Instead
                </button>
              )}
              <a
                href="/"
                data-testid="keep-booking"
                className={`py-4 text-center mono-label border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 transition ${canReschedule ? "" : "sm:col-span-2"}`}
              >
                Keep My Appointment
              </a>
            </div>
            {!canReschedule && (
              <p className="mt-3 text-xs text-stone-500 text-center" data-testid="reschedule-blocked-note">
                Appointments cannot be rescheduled if it is under 24 hours away from your booking time. Please contact us to discuss options.
              </p>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function ReschedulePanel({ bid, token, duration, currentDate, onCancel, onDone }) {
  const minDate = useMemo(() => {
    // At least 24h in the future.
    const dt = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return dt.toISOString().split("T")[0];
  }, []);
  const [date, setDate] = useState(currentDate >= minDate ? currentDate : minDate);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingTime, setPendingTime] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setLoading(true);
    setPendingTime(null);
    api.get("/slots", { params: { date, duration } })
      .then((r) => {
        // Filter out slots that would fall within the next 24h.
        const cutoff = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const filtered = (r.data.slots || []).filter((t) => {
          const [h, m] = t.split(":").map(Number);
          const [Y, M, D] = date.split("-").map(Number);
          const slotDt = new Date(Date.UTC(Y, M - 1, D, h, m));
          return slotDt >= cutoff;
        });
        setSlots(filtered);
      })
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date, duration]);

  const commit = async () => {
    if (!pendingTime) return;
    setConfirming(true);
    try {
      const r = await api.post(`/bookings/${bid}/self-reschedule?token=${encodeURIComponent(token)}`, { date, time: pendingTime });
      toast.success(`Rescheduled to ${r.data.to.date} · ${r.data.to.time}`);
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
    }
    setConfirming(false);
  };

  const prettyDate = (iso) => {
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="mt-8 border hairline bg-white p-6" data-testid="self-reschedule-panel">
      <label className="mono-label text-[11px]">Choose a new date</label>
      <input
        data-testid="self-reschedule-date"
        type="date"
        min={minDate}
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="mt-2 block w-full text-left border hairline bg-[#FAF9F6] px-3 py-3 text-sm focus:outline-none focus:border-stone-900 appearance-none [font-family:inherit]"
      />
      <p className="mono-label text-[10px] mt-5 mb-2">Available slots ({slots.length})</p>
      {loading ? (
        <p className="text-xs text-stone-500">Loading…</p>
      ) : slots.length === 0 ? (
        <p className="text-xs text-stone-500">No slots on this day — please pick another date.</p>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
          {slots.map((t) => {
            const selected = pendingTime === t;
            return (
              <button
                key={t}
                data-testid={`self-reschedule-to-${t}`}
                onClick={() => setPendingTime(t)}
                className={`mono-label border hairline px-3 py-2 text-xs transition ${selected ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:bg-stone-100"}`}
                style={selected ? { color: "#fff" } : undefined}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          data-testid="cancel-reschedule"
          onClick={onCancel}
          className="mono-label text-stone-600 hover:text-stone-900 text-xs"
        >
          ← Back
        </button>
        {pendingTime && (
          <p className="text-[11px] text-stone-500 hidden sm:block">
            Selected: <span className="text-stone-900 font-medium">{prettyDate(date)} · {pendingTime}</span>
          </p>
        )}
      </div>

      {/* Second-step confirmation — prevents accidental slot booking */}
      {pendingTime && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
          data-testid="reschedule-confirm-modal"
          onClick={() => !confirming && setPendingTime(null)}
        >
          <div
            className="bg-[#FAF9F6] w-full max-w-md border hairline p-6 sm:p-8 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mono-label text-stone-500 text-[10px]">CONFIRM RESCHEDULE</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl font-light text-stone-900">Move your appointment to this new time?</h2>
            <div className="mt-6 border-t hairline pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-600">From</span><span className="line-through text-stone-400">{prettyDate(currentDate)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">To</span><span className="font-medium text-stone-900">{prettyDate(date)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Time</span><span className="font-medium text-stone-900">{pendingTime}</span></div>
            </div>
            <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">Your booking reference and deposit stay the same. A confirmation email will be sent to you.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                data-testid="reschedule-cancel-modal"
                onClick={() => setPendingTime(null)}
                disabled={confirming}
                className="mono-label border border-stone-300 text-stone-700 py-3 text-xs hover:border-stone-900 hover:text-stone-900 transition disabled:opacity-40"
              >
                Not yet
              </button>
              <button
                data-testid="reschedule-confirm-modal-yes"
                onClick={commit}
                disabled={confirming}
                className="mono-label bg-stone-900 text-white py-3 text-xs hover:bg-stone-700 transition disabled:opacity-40"
                style={{ color: "#fff" }}
              >
                {confirming ? "Rescheduling…" : "Yes, confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
